import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Grid,
} from "@mui/material";
import PropTypes from "prop-types";
import { filePropTypesByModelKey } from "utils/filePropTypes";
import {
  fileSoftDeleteByModelKey,
  fileUpdateByModelKey,
} from "api/FileApiRegistry";
import FileDialog from "./FileDialog/FileDialog";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useAuth } from "../../context/AuthContext";

import { normalizeInitialValues } from "../../utils/normalizeInitialValues";

// Componente auxiliar por tipo de archivo
const isEmpty = (v) => v === null || v === undefined || v === "";

const FileDetailContent = ({ modelKey, file }) => {
  if (modelKey === "cmam_toferda_files") {
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        {/* --- General Info --- */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Sample ID:</strong> {file.sample_ID}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Beam Ion:</strong>{" "}
              {`${file.beam_isotope?.symbol} - ${file.beam_isotope?.mass_number}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Charge State:</strong> {file.beam_charge_state}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Beam Energy:</strong>{" "}
              {`${file.beam_energy} ${file.beam_energy_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Beam Current:</strong>{" "}
              {`${file.beam_current} ${file.beam_current_units}`}
            </Typography>
          </Grid>
          {/* <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Inj. Energy:</strong>{" "}
              {`${file.injection_energy} ${file.injection_energy_units}`}
            </Typography>
          </Grid> */}
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Terminal Voltage:</strong>{" "}
              {`${file.terminal_voltage} ${file.terminal_voltage_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Electromagnet Current:</strong>{" "}
              {`${file.electromagnet_current} ${file.electromagnet_current_units}`}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Incident Angle:</strong>{" "}
              {`${file.incident_angle} ${file.incident_angle_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Exit Angle:</strong>{" "}
              {`${file.exit_angle} ${file.exit_angle_units}`}
            </Typography>
          </Grid>

          {file.coincidence_window != null && (
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Coincidence Window:</strong>{" "}
                {`${file.coincidence_window} ${file.coincidence_window_units}`}
              </Typography>
            </Grid>
          )}
        </Grid>

        <Divider />

        {/* --- TOF Detector --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          TOF Detector
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Name:</strong> {file.TOF_detector?.name}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Type:</strong> {file.TOF_detector?.type}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Radiation:</strong> {file.TOF_detector?.radiation_type}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Config File:</strong>{" "}
              <a
                href={file.TOF_detector?.config_file}
                target="_blank"
                rel="noopener noreferrer"
              >
                View File
              </a>
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Distance:</strong>{" "}
              {`${file.sample_TOF_detector_distance} ${file.sample_TOF_detector_distance_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Flight Length:</strong>{" "}
              {`${file.length_of_flight} ${file.length_of_flight_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>BIAS Voltage:</strong>{" "}
              {`${file.TOF_station_BIAS_voltage} ${file.TOF_station_BIAS_voltage_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>TAC Range:</strong>{" "}
              {`${file.TOF_TAC_range} ${file.TOF_TAC_range_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>TAC Multiplier:</strong> {file.TOF_TAC_multiplier}
            </Typography>
          </Grid>

          {/* Additional  */}
          {file.TOF_ADC_gain != null && (
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>ADC Gain:</strong> {file.TOF_ADC_gain}
              </Typography>
            </Grid>
          )}

          {file.TOF_ADC_range != null && (
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>ADC Range:</strong> {file.TOF_ADC_range}
              </Typography>
            </Grid>
          )}
        </Grid>

        <Divider />

        {/* --- Energy Detector --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Energy Detector
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Name:</strong> {file.energy_detector?.name}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Type:</strong> {file.energy_detector?.type}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Radiation:</strong> {file.energy_detector?.radiation_type}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Config File:</strong>{" "}
              <a
                href={file.energy_detector?.config_file}
                target="_blank"
                rel="noopener noreferrer"
              >
                View File
              </a>
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Distance:</strong>{" "}
              {`${file.sample_energy_detector_distance} ${file.sample_energy_detector_distance_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>BIAS Voltage:</strong>{" "}
              {`${file.energy_detector_BIAS_voltage} ${file.energy_detector_BIAS_voltage_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Shaping Time:</strong>{" "}
              {`${file.energy_shaping_time} ${file.energy_shaping_time_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Coarse Gain:</strong> {file.energy_amplifier_coarse_gain}
            </Typography>
          </Grid>

          {/* Additional */}
          {file.energy_amplifier_fine_gain != null && (
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Fine Gain:</strong> {file.energy_amplifier_fine_gain}
              </Typography>
            </Grid>
          )}
          {file.energy_ADC_gain != null && (
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>ADC Gain:</strong> {file.energy_ADC_gain}
              </Typography>
            </Grid>
          )}
          {file.energy_ADC_range != null && (
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>ADC Range:</strong> {file.energy_ADC_range}
              </Typography>
            </Grid>
          )}
        </Grid>

        <Divider />

        {/* --- Final info --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Additional Info.
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={2.5}>
            <Typography variant="body2">
              <strong>Created by:</strong> {file.created_by.username}
            </Typography>
          </Grid>
          <Grid item xs={4.75}>
            <Typography variant="body2">
              <strong>Created at:</strong>{" "}
              {new Date(file.created_at).toLocaleString()}
            </Typography>
          </Grid>

          {file.updated_at && (
            <Grid item xs={4.75}>
              <Typography variant="body2">
                <strong>Updated at:</strong>{" "}
                {new Date(file.updated_at).toLocaleString()}
              </Typography>
            </Grid>
          )}
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2">
              <strong>Notes:</strong> {file.notes || "—"}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    );
  }
  if (modelKey === "cna_std_erda_files") {
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        {/* --- General Info --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          General Parameters
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Sample ID:</strong> {file.sample_ID}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Ion:</strong>{" "}
              {`${file.beam_isotope?.symbol} - ${file.beam_isotope?.mass_number}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Charge State:</strong> {file.beam_charge_state}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Energy:</strong>{" "}
              {`${file.beam_energy} ${file.beam_energy_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Current chopper:</strong>{" "}
              {`${file.beam_current} ${file.beam_current_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Inj. Energy:</strong>{" "}
              {`${file.injection_energy} ${file.injection_energy_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Term. Volt.:</strong>{" "}
              {`${file.terminal_voltage} ${file.terminal_voltage_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Magn. Field:</strong>{" "}
              {`${file.magnetic_field} ${file.magnetic_field_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Acc. Charge:</strong>{" "}
              {`${file.accumulated_charge} ${file.accumulated_charge_units}`}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Beam Geometry --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Beam Geometry
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Shape:</strong> {file.beam_shape || "—"}
            </Typography>
          </Grid>

          {file.beam_shape === "rectangular" && (
            <>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Width:</strong>{" "}
                  {`${file.beam_shape_width} ${
                    file.beam_shape_width_units || ""
                  }`}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Height:</strong>{" "}
                  {`${file.beam_shape_height} ${
                    file.beam_shape_height_units || ""
                  }`}
                </Typography>
              </Grid>
            </>
          )}

          {file.beam_shape === "circular" && (
            <Grid item xs={4}>
              <Typography variant="body2">
                <strong>Diameter:</strong>{" "}
                {`${file.beam_shape_diameter} ${
                  file.beam_shape_diameter_units || ""
                }`}
              </Typography>
            </Grid>
          )}
        </Grid>

        <Divider />

        {/* --- Angles --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Angles
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Incident:</strong>{" "}
              {`${file.incident_angle} ${file.incident_angle_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Exit:</strong>{" "}
              {`${file.exit_angle} ${file.exit_angle_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Scattering:</strong>{" "}
              {`${file.scattering_angle} ${file.scattering_angle_units}`}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Filter --- */}
        {file.is_filter_present && (
          <>
            <Typography
              variant="h6"
              sx={{ color: "primary", fontWeight: "bold" }}
            >
              Filter
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Present:</strong> Yes
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Material:</strong> {file.filter_material || "—"}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Thickness:</strong>{" "}
                  {`${file.filter_thickness ?? "—"} ${
                    file.filter_thickness_units || ""
                  }`}
                </Typography>
              </Grid>
            </Grid>
          </>
        )}

        <Divider />

        {/* --- Detector Settings --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Detector
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Typography variant="body2">
              <strong>Name:</strong> {file.detector?.name}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2">
              <strong>Distance:</strong>{" "}
              {`${file.sample_detector_distance} ${file.sample_detector_distance_units}`}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2">
              <strong>BIAS:</strong>{" "}
              {`${file.detector_BIAS_voltage} ${file.detector_BIAS_voltage_units}`}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2">
              <strong>Inv. Current:</strong>{" "}
              {`${file.detector_inverse_current} ${file.detector_inverse_current_units}`}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Amplifier --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Amplifier
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Shaping Time:</strong>{" "}
              {`${file.shaping_time} ${file.shaping_time_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Peaking Time:</strong>{" "}
              {`${file.peaking_time} ${file.peaking_time_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Coarse Gain:</strong> {file.amplifier_coarse_gain}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Fine Gain:</strong> {file.amplifier_fine_gain}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>ADC Gain:</strong> {file.adc_gain}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>ADC Range:</strong> {file.adc_range}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Final info --- */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Notes:</strong> {file.notes || "—"}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    );
  }
  if (modelKey === "cna_std_nra_files") {
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        {/* --- General Info --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          General Parameters
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Sample ID:</strong> {file.sample_ID}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Reaction:</strong> {file.reaction.reaction_notation}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Ion:</strong>{" "}
              {`${file.beam_isotope?.symbol} - ${file.beam_isotope?.mass_number}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Charge State:</strong> {file.beam_charge_state}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Energy:</strong>{" "}
              {`${file.beam_energy} ${file.beam_energy_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Current:</strong>{" "}
              {`${file.beam_current} ${file.beam_current_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Inj. Energy:</strong>{" "}
              {`${file.injection_energy} ${file.injection_energy_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Term. Volt.:</strong>{" "}
              {`${file.terminal_voltage} ${file.terminal_voltage_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Magn. Field:</strong>{" "}
              {`${file.magnetic_field} ${file.magnetic_field_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Acc. Charge:</strong>{" "}
              {`${file.accumulated_charge} ${file.accumulated_charge_units}`}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Beam Geometry --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Beam Geometry
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Shape:</strong> {file.beam_shape || "—"}
            </Typography>
          </Grid>

          {file.beam_shape === "rectangular" && (
            <>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Width:</strong>{" "}
                  {`${file.beam_shape_width} ${
                    file.beam_shape_width_units || ""
                  }`}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Height:</strong>{" "}
                  {`${file.beam_shape_height} ${
                    file.beam_shape_height_units || ""
                  }`}
                </Typography>
              </Grid>
            </>
          )}

          {file.beam_shape === "circular" && (
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Diameter:</strong>{" "}
                {`${file.beam_shape_diameter} ${
                  file.beam_shape_diameter_units || ""
                }`}
              </Typography>
            </Grid>
          )}
        </Grid>

        <Divider />

        {/* --- Angles --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Angles
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Incident:</strong>{" "}
              {`${file.incident_angle} ${file.incident_angle_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Exit:</strong>{" "}
              {`${file.exit_angle} ${file.exit_angle_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Scattering:</strong>{" "}
              {`${file.scattering_angle} ${file.scattering_angle_units}`}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Filter --- */}
        {file.is_filter_present && (
          <>
            <Typography
              variant="h6"
              sx={{ color: "primary", fontWeight: "bold" }}
            >
              Filter
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Present:</strong> Yes
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Material:</strong> {file.filter_material || "—"}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Thickness:</strong>{" "}
                  {`${file.filter_thickness ?? "—"} ${
                    file.filter_thickness_units || ""
                  }`}
                </Typography>
              </Grid>
            </Grid>
          </>
        )}

        <Divider />

        {/* --- Detector Settings --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Detector
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Typography variant="body2">
              <strong>Name:</strong> {file.detector?.name}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2">
              <strong>Distance:</strong>{" "}
              {`${file.sample_detector_distance} ${file.sample_detector_distance_units}`}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2">
              <strong>BIAS:</strong>{" "}
              {`${file.detector_BIAS_voltage} ${file.detector_BIAS_voltage_units}`}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2">
              <strong>Inv. Current:</strong>{" "}
              {`${file.detector_inverse_current} ${file.detector_inverse_current_units}`}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Amplifier --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Amplifier
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Shaping Time:</strong>{" "}
              {`${file.shaping_time} ${file.shaping_time_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Peaking Time:</strong>{" "}
              {`${file.peaking_time} ${file.peaking_time_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Coarse Gain:</strong> {file.amplifier_coarse_gain}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Fine Gain:</strong> {file.amplifier_fine_gain}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>ADC Gain:</strong> {file.adc_gain}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>ADC Range:</strong> {file.adc_range}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Final info --- */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Notes:</strong> {file.notes || "—"}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    );
  }
  if (modelKey === "cna_std_pige_files") {
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        {/* --- General Info --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          General Parameters
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Sample ID:</strong> {file.sample_ID}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Ion:</strong>{" "}
              {`${file.beam_isotope?.symbol} - ${file.beam_isotope?.mass_number}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Charge State:</strong> {file.beam_charge_state}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Energy:</strong>{" "}
              {`${file.beam_energy} ${file.beam_energy_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Current:</strong>{" "}
              {`${file.beam_current} ${file.beam_current_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Inj. Energy:</strong>{" "}
              {`${file.injection_energy} ${file.injection_energy_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Term. Volt.:</strong>{" "}
              {`${file.terminal_voltage} ${file.terminal_voltage_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Magn. Field:</strong>{" "}
              {`${file.magnetic_field} ${file.magnetic_field_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Acc. Charge:</strong>{" "}
              {`${file.accumulated_charge} ${file.accumulated_charge_units}`}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Beam Geometry --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Beam Geometry
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Shape:</strong> {file.beam_shape || "—"}
            </Typography>
          </Grid>

          {file.beam_shape === "rectangular" && (
            <>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Width:</strong>{" "}
                  {`${file.beam_shape_width} ${
                    file.beam_shape_width_units || ""
                  }`}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Height:</strong>{" "}
                  {`${file.beam_shape_height} ${
                    file.beam_shape_height_units || ""
                  }`}
                </Typography>
              </Grid>
            </>
          )}

          {file.beam_shape === "circular" && (
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Diameter:</strong>{" "}
                {`${file.beam_shape_diameter} ${
                  file.beam_shape_diameter_units || ""
                }`}
              </Typography>
            </Grid>
          )}
        </Grid>

        <Divider />

        {/* --- Angles --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Angles
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Incident:</strong>{" "}
              {`${file.incident_angle} ${file.incident_angle_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Exit:</strong>{" "}
              {`${file.exit_angle} ${file.exit_angle_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Scattering:</strong>{" "}
              {`${file.scattering_angle} ${file.scattering_angle_units}`}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Filter --- */}
        {file.is_filter_present && (
          <>
            <Typography
              variant="h6"
              sx={{ color: "primary", fontWeight: "bold" }}
            >
              Filter
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Present:</strong> Yes
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Material:</strong> {file.filter_material || "—"}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Thickness:</strong>{" "}
                  {`${file.filter_thickness ?? "—"} ${
                    file.filter_thickness_units || ""
                  }`}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Diameter:</strong>{" "}
                  {`${file.filter_diameter ?? "—"} ${
                    file.filter_diameter_units || ""
                  }`}
                </Typography>
              </Grid>
            </Grid>
          </>
        )}

        <Divider />

        {/* --- Detector Settings --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Detector
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Typography variant="body2">
              <strong>Name:</strong> {file.detector?.name}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2">
              <strong>Distance:</strong>{" "}
              {`${file.sample_detector_distance} ${file.sample_detector_distance_units}`}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2">
              <strong>BIAS:</strong>{" "}
              {`${file.detector_BIAS_voltage} ${file.detector_BIAS_voltage_units}`}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2">
              <strong>Inv. Current:</strong>{" "}
              {`${file.detector_inverse_current} ${file.detector_inverse_current_units}`}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Amplifier --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Amplifier
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Shaping Time:</strong>{" "}
              {`${file.shaping_time} ${file.shaping_time_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Peaking Time:</strong>{" "}
              {`${file.peaking_time} ${file.peaking_time_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Coarse Gain:</strong> {file.amplifier_coarse_gain}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Fine Gain:</strong> {file.amplifier_fine_gain}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>ADC Gain:</strong> {file.adc_gain}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>ADC Range:</strong> {file.adc_range}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Final info --- */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Notes:</strong> {file.notes || "—"}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    );
  }
  if (modelKey === "cna_std_pixe_files") {
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        {/* --- General Info --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          General Parameters
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Sample ID:</strong> {file.sample_ID}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Ion:</strong>{" "}
              {`${file.beam_isotope?.symbol} - ${file.beam_isotope?.mass_number}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Charge State:</strong> {file.beam_charge_state}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Energy:</strong>{" "}
              {`${file.beam_energy} ${file.beam_energy_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Current:</strong>{" "}
              {`${file.beam_current} ${file.beam_current_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Inj. Energy:</strong>{" "}
              {`${file.injection_energy} ${file.injection_energy_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Term. Volt.:</strong>{" "}
              {`${file.terminal_voltage} ${file.terminal_voltage_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Magn. Field:</strong>{" "}
              {`${file.magnetic_field} ${file.magnetic_field_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Acc. Charge:</strong>{" "}
              {`${file.accumulated_charge} ${file.accumulated_charge_units}`}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Beam Geometry --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Beam Geometry
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Shape:</strong> {file.beam_shape || "—"}
            </Typography>
          </Grid>

          {file.beam_shape === "rectangular" && (
            <>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Width:</strong>{" "}
                  {`${file.beam_shape_width} ${
                    file.beam_shape_width_units || ""
                  }`}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Height:</strong>{" "}
                  {`${file.beam_shape_height} ${
                    file.beam_shape_height_units || ""
                  }`}
                </Typography>
              </Grid>
            </>
          )}

          {file.beam_shape === "circular" && (
            <Grid item xs={4}>
              <Typography variant="body2">
                <strong>Diameter:</strong>{" "}
                {`${file.beam_shape_diameter} ${
                  file.beam_shape_diameter_units || ""
                }`}
              </Typography>
            </Grid>
          )}
        </Grid>

        <Divider />

        {/* --- Angles --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Angles
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Incident:</strong>{" "}
              {`${file.incident_angle} ${file.incident_angle_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Exit:</strong>{" "}
              {`${file.exit_angle} ${file.exit_angle_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Scattering:</strong>{" "}
              {`${file.scattering_angle} ${file.scattering_angle_units}`}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Filter --- */}
        {file.is_filter_present && (
          <>
            <Typography
              variant="h6"
              sx={{ color: "primary", fontWeight: "bold" }}
            >
              Filter
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Present:</strong> Yes
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Material:</strong> {file.filter_material || "—"}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Thickness:</strong>{" "}
                  {`${file.filter_thickness ?? "—"} ${
                    file.filter_thickness_units || ""
                  }`}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Diameter:</strong>{" "}
                  {`${file.filter_diameter ?? "—"} ${
                    file.filter_diameter_units || ""
                  }`}
                </Typography>
              </Grid>
            </Grid>
          </>
        )}

        <Divider />

        {/* --- Detector Settings --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Detector
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Typography variant="body2">
              <strong>Name:</strong> {file.detector?.name}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2">
              <strong>Distance:</strong>{" "}
              {`${file.sample_detector_distance} ${file.sample_detector_distance_units}`}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2">
              <strong>BIAS:</strong>{" "}
              {`${file.detector_BIAS_voltage} ${file.detector_BIAS_voltage_units}`}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2">
              <strong>Inv. Current:</strong>{" "}
              {`${file.detector_inverse_current} ${file.detector_inverse_current_units}`}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Amplifier --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Amplifier
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Shaping Time:</strong>{" "}
              {`${file.shaping_time} ${file.shaping_time_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Peaking Time:</strong>{" "}
              {`${file.peaking_time} ${file.peaking_time_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Coarse Gain:</strong> {file.amplifier_coarse_gain}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Fine Gain:</strong> {file.amplifier_fine_gain}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>ADC Gain:</strong> {file.adc_gain}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>ADC Range:</strong> {file.adc_range}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Final info --- */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Notes:</strong> {file.notes || "—"}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    );
  }
  if (modelKey === "cna_std_rbs_files") {
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        {/* --- General Info --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          General Parameters
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Sample ID:</strong> {file.sample_ID}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Ion:</strong>{" "}
              {`${file.beam_isotope?.symbol} - ${file.beam_isotope?.mass_number}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Charge State:</strong> {file.beam_charge_state}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Energy:</strong>{" "}
              {`${file.beam_energy} ${file.beam_energy_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Current:</strong>{" "}
              {`${file.beam_current} ${file.beam_current_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Inj. Energy:</strong>{" "}
              {`${file.injection_energy} ${file.injection_energy_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Term. Volt.:</strong>{" "}
              {`${file.terminal_voltage} ${file.terminal_voltage_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Magn. Field:</strong>{" "}
              {`${file.magnetic_field} ${file.magnetic_field_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Acc. Charge:</strong>{" "}
              {`${file.accumulated_charge} ${file.accumulated_charge_units}`}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Beam Geometry --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Beam Geometry
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Shape:</strong> {file.beam_shape || "—"}
            </Typography>
          </Grid>

          {file.beam_shape === "rectangular" && (
            <>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Width:</strong>{" "}
                  {`${file.beam_shape_width} ${
                    file.beam_shape_width_units || ""
                  }`}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Height:</strong>{" "}
                  {`${file.beam_shape_height} ${
                    file.beam_shape_height_units || ""
                  }`}
                </Typography>
              </Grid>
            </>
          )}

          {file.beam_shape === "circular" && (
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Diameter:</strong>{" "}
                {`${file.beam_shape_diameter} ${
                  file.beam_shape_diameter_units || ""
                }`}
              </Typography>
            </Grid>
          )}
        </Grid>

        <Divider />

        {/* --- Angles --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Angles
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Incident:</strong>{" "}
              {`${file.incident_angle} ${file.incident_angle_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Exit:</strong>{" "}
              {`${file.exit_angle} ${file.exit_angle_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Scattering:</strong>{" "}
              {`${file.scattering_angle} ${file.scattering_angle_units}`}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Filter --- */}
        {file.is_filter_present && (
          <>
            <Typography
              variant="h6"
              sx={{ color: "primary", fontWeight: "bold" }}
            >
              Filter
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Present:</strong> Yes
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Material:</strong> {file.filter_material || "—"}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2">
                  <strong>Thickness:</strong>{" "}
                  {`${file.filter_thickness ?? "—"} ${
                    file.filter_thickness_units || ""
                  }`}
                </Typography>
              </Grid>
            </Grid>
          </>
        )}

        <Divider />

        {/* --- Detector Settings --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Detector
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Typography variant="body2">
              <strong>Name:</strong> {file.detector?.name}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2">
              <strong>Distance:</strong>{" "}
              {`${file.sample_detector_distance} ${file.sample_detector_distance_units}`}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2">
              <strong>BIAS:</strong>{" "}
              {`${file.detector_BIAS_voltage} ${file.detector_BIAS_voltage_units}`}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2">
              <strong>Inv. Current:</strong>{" "}
              {`${file.detector_inverse_current} ${file.detector_inverse_current_units}`}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Amplifier --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Amplifier
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Shaping Time:</strong>{" "}
              {`${file.shaping_time} ${file.shaping_time_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Peaking Time:</strong>{" "}
              {`${file.peaking_time} ${file.peaking_time_units}`}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Coarse Gain:</strong> {file.amplifier_coarse_gain}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Fine Gain:</strong> {file.amplifier_fine_gain}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>ADC Gain:</strong> {file.adc_gain}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>ADC Range:</strong> {file.adc_range}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Final info --- */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Notes:</strong> {file.notes || "—"}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    );
  }
  if (modelKey === "cna_ub_ibic_files") {
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        {/* --- General Info --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          General Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>File Name:</strong> {file.name}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Beam Ion:</strong>{" "}
              {`${file.beam_isotope?.symbol} - ${file.beam_isotope?.mass_number}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Charge State:</strong> {file.beam_charge_state}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Beam Energy:</strong>{" "}
              {`${file.beam_energy} ${file.beam_energy_units}`}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Accelerator --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Accelerator Settings
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Injection Energy:</strong>{" "}
              {`${file.injection_energy} ${file.injection_energy_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Terminal Voltage:</strong>{" "}
              {`${file.terminal_voltage} ${file.terminal_voltage_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Magnetic Field:</strong>{" "}
              {`${file.magnetic_field} ${file.magnetic_field_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>High Voltage Source:</strong> {file.high_voltage_source}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Detector & Electronics --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Detector & Electronics
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Detector ID:</strong> {file.detector_ID}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Preamp Modified:</strong>{" "}
              {file.is_preamplifier_modified ? "Yes" : "No"}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Preamplifier ID:</strong> {file.preamplifier_ID}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Amplifier Coarse Gain:</strong>{" "}
              {file.amplifier_coarse_gain}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Amplifier Fine Gain:</strong> {file.amplifier_fine_gain}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Shaping Time:</strong>{" "}
              {`${file.shaping_time} ${file.shaping_time_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Peaking Time:</strong>{" "}
              {`${file.peaking_time} ${file.peaking_time_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Polarity:</strong> {file.polarity}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>ADC ID:</strong> {file.ADC_ID}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Scan & Measurement --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Scan & Measurement
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Scan Size:</strong>{" "}
              {`${file.scan_size} ${file.scan_size_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Time per Pixel:</strong>{" "}
              {`${file.time_per_pixel} ${file.time_per_pixel_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Count Rate:</strong>{" "}
              {`${file.count_rate} ${file.count_rate_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>BIAS Voltage:</strong>{" "}
              {`${file.BIAS_voltage} ${file.BIAS_voltage_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Incident Angle:</strong>{" "}
              {`${file.incident_angle} ${file.incident_angle_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Channel:</strong> {file.channel}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Final info --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Additional Info
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Created by:</strong> {file.created_by?.username}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>Created at:</strong>{" "}
              {new Date(file.created_at).toLocaleString()}
            </Typography>
          </Grid>
          {file.updated_at && (
            <Grid item xs={4}>
              <Typography variant="body2">
                <strong>Updated at:</strong>{" "}
                {new Date(file.updated_at).toLocaleString()}
              </Typography>
            </Grid>
          )}
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2">
              <strong>Notes:</strong> {file.notes || "—"}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    );
  }
  if (modelKey === "cna_ub_pixe_files") {
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        {/* --- General Info --- */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Sample ID:</strong> {file.sample_ID}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Beam Ion:</strong>{" "}
              {`${file.beam_isotope?.symbol} - ${file.beam_isotope?.mass_number}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Charge State:</strong> {file.beam_charge_state}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Beam Energy:</strong>{" "}
              {`${file.beam_energy} ${file.beam_energy_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Injection Energy:</strong>{" "}
              {`${file.injection_energy} ${file.injection_energy_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Terminal Voltage:</strong>{" "}
              {`${file.terminal_voltage} ${file.terminal_voltage_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Magnetic Field:</strong>{" "}
              {`${file.magnetic_field} ${file.magnetic_field_units}`}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Scan Parameters --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Scan Parameters
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Scan Size:</strong>{" "}
              {`${file.scan_size} ${file.scan_size_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Time per Pixel:</strong>{" "}
              {`${file.time_per_pixel} ${file.time_per_pixel_units}`}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Additional Info --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Additional Info.
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={2.5}>
            <Typography variant="body2">
              <strong>Created by:</strong> {file.created_by?.username}
            </Typography>
          </Grid>
          <Grid item xs={4.75}>
            <Typography variant="body2">
              <strong>Created at:</strong>{" "}
              {new Date(file.created_at).toLocaleString()}
            </Typography>
          </Grid>
          {file.updated_at && (
            <Grid item xs={4.75}>
              <Typography variant="body2">
                <strong>Updated at:</strong>{" "}
                {new Date(file.updated_at).toLocaleString()}
              </Typography>
            </Grid>
          )}
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2">
              <strong>Notes:</strong> {file.notes || "—"}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    );
  }
  if (modelKey === "cna_ub_tribic_files") {
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        {/* --- General Info --- */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Initial File:</strong> {file.initial_file}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Final File:</strong> {file.final_file}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Beam Ion:</strong>{" "}
              {`${file.beam_isotope?.symbol} - ${file.beam_isotope?.mass_number}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Charge State:</strong> {file.beam_charge_state}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Beam Energy:</strong>{" "}
              {`${file.beam_energy} ${file.beam_energy_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Injection Energy:</strong>{" "}
              {`${file.injection_energy} ${file.injection_energy_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Terminal Voltage:</strong>{" "}
              {`${file.terminal_voltage} ${file.terminal_voltage_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Magnetic Field:</strong>{" "}
              {`${file.magnetic_field} ${file.magnetic_field_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Averaging:</strong> {file.averaging}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Amplifier and Acquisition --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Amplifier & Acquisition
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Amplifier:</strong> {file.amplifier}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Coarse Gain:</strong> {file.amplifier_coarse_gain}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Fine Gain:</strong> {file.amplifier_fine_gain}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Scan Size:</strong>{" "}
              {`${file.scan_size} ${file.scan_size_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Time per Pixel:</strong>{" "}
              {`${file.time_per_pixel} ${file.time_per_pixel_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Oscilloscope:</strong> {file.oscilloscope_ID}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>BIAS Voltage:</strong>{" "}
              {`${file.BIAS_voltage} ${file.BIAS_voltage_units}`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Punctual Shot:</strong>{" "}
              {file.is_puntual_shot
                ? "Yes"
                : file.is_puntual_shot === false
                ? "No"
                : "—"}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* --- Final Info --- */}
        <Typography variant="h6" sx={{ color: "primary", fontWeight: "bold" }}>
          Additional Info
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={2.5}>
            <Typography variant="body2">
              <strong>Created by:</strong> {file.created_by?.username}
            </Typography>
          </Grid>
          <Grid item xs={4.75}>
            <Typography variant="body2">
              <strong>Created at:</strong>{" "}
              {new Date(file.created_at).toLocaleString()}
            </Typography>
          </Grid>
          {file.updated_at && (
            <Grid item xs={4.75}>
              <Typography variant="body2">
                <strong>Updated at:</strong>{" "}
                {new Date(file.updated_at).toLocaleString()}
              </Typography>
            </Grid>
          )}
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2">
              <strong>Notes:</strong> {file.notes || "—"}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return <Typography>No detail view available for this file type.</Typography>;
};

const FileDetailView = ({ open, onClose, file, modelKey, onFileUpdated }) => {
  // No error si file es null al principio
  const FilePropType = filePropTypesByModelKey[modelKey] || PropTypes.object;

  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const isOwner =
    file?.created_by?.id && user?.id && file.created_by.id === user.id;
  const isSuperuser = !!user?.is_superuser;
  const canEditOrDelete = isOwner || isSuperuser;

  const handleDelete = async () => {
    const softDeleteFn = fileSoftDeleteByModelKey[modelKey];
    if (!softDeleteFn) return;

    try {
      await softDeleteFn(file.id);
      enqueueSnackbar("File deleted", { variant: "success" });
      onClose(); // cerrar detalle
      onFileUpdated?.();
    } catch (err) {
      console.error("Error deleting file:", err);
      enqueueSnackbar(
        `Error deleting file: ${err.response?.data?.detail || err.message}`,
        {
          variant: "error",
        },
      );
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          File Details{file?.name ? `: ${file.name}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {file ? (
            <FileDetailContent modelKey={modelKey} file={file} />
          ) : (
            <Typography variant="body2">Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          {canEditOrDelete && (
            <>
              <Button
                onClick={(e) => {
                  e.currentTarget.blur(); // 🔥 clave
                  setEditDialogOpen(true);
                }}
                color="primary"
                variant="contained"
              >
                Edit
              </Button>
              <Button onClick={handleDelete} color="error" variant="contained">
                Delete
              </Button>
            </>
          )}
          <Button onClick={onClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🔁 Diálogo reutilizable para editar */}
      {canEditOrDelete && editDialogOpen && (
        <FileDialog
          open={editDialogOpen}
          modelKey={modelKey}
          mode="edit" // ⬅️ importante
          initialValues={normalizeInitialValues(file, { keepName: true })}
          onCancel={() => setEditDialogOpen(false)}
          onSubmit={(updatedModelKey, updatedFile) => {
            setEditDialogOpen(false);
            onFileUpdated?.(); // actualiza DataGrid
            onClose(); // cierra detalles
          }}
          modelLabels={{}} // si los usas
        />
      )}
    </>
  );
};

FileDetailView.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  modelKey: PropTypes.string.isRequired,
  onFileUpdated: PropTypes.func,
  file: PropTypes.oneOfType([
    PropTypes.oneOf([null]), // permite null
    PropTypes.shape({}), // mínima validación estructural
  ]),
};

FileDetailContent.propTypes = {
  modelKey: PropTypes.string.isRequired,
  file: (props, propName, componentName) => {
    const modelKey = props.modelKey;
    const file = props[propName];

    const fileShape = filePropTypesByModelKey[modelKey];

    if (!fileShape) {
      return null; // No validamos si no hay forma definida aún
    }

    return PropTypes.checkPropTypes(
      { [propName]: fileShape },
      { [propName]: file },
      propName,
      componentName,
    );
  },
};

export default FileDetailView;
